import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { DialogDismissGuardDirective } from '@shared/ui';

import { PipelineEditorEffects, PipelineEditorOutcome } from '../../../effects/pipeline-editor.effects';
import { EDITOR_COMMIT_MODE } from '../../editor-commit-mode.token';

import { PipelineEditorComponent } from './pipeline-editor.component';

/**
 * Dialog host for `PipelineEditorComponent`.
 *
 * Bridges the shared `PipelineEditorEffects.outcome$` onto the dialog's close
 * payload — closes with the `PipelineEditorOutcome` (the *action* the user
 * took), never the draft. The draft stays in the shared state as `draft$`;
 * the consumer reads it from there on `saved`. Backdrop click / Escape do not
 * close the dialog directly: `DialogDismissGuardDirective` routes them through
 * the editor's `onCancel()` (the same discard-confirm flow as the Cancel
 * button), so the dialog always closes with an explicit outcome. Consumers'
 * `undefined` handling remains only as a harmless defensive branch.
 *
 * The wrapper depends on `PipelineEditorEffects` (not the facade) on purpose:
 * the effects class is the session seam — the consumer seeds the session
 * through it (`loadPipeline(...)` / `seedFromDraft(...)`) and it emits the
 * one-shot outcome the wrapper closes on. The editor's facade is internal to
 * `PipelineEditorComponent` and is not the consumer's to own — facades are
 * per-component (see UI-LAYER.md "Facade classes are not re-usable").
 *
 * The wrapper does **not** seed state and declares **no session providers**
 * (its only provider is `EDITOR_COMMIT_MODE: 'stage'` — a per-wrapper UI
 * concern: a dialog stages, so the editor's confirm button reads "Continue").
 * The
 * consumer (e.g. `method-pipeline-manager.component.ts`'s host) must spread
 * `pipelineEditorSessionProviders` at its own `providers: []` (with the
 * `pipelineEditorCascadeProviders` label/cascade tier reachable above), call
 * `PipelineEditorEffects.loadPipeline(pipelineId | null)` (null = "new
 * pipeline" create mode) *before* opening, and pass `injector: this.injector`
 * to `MatDialog.open` so the consumer's `PipelineEditorEffects` reaches the
 * dialog's injector chain. On `saved` the consumer reads the draft from the
 * shared `draft$` and stages it; on cancel/dismiss it discards.
 *
 * Browser back: the routed page hosting this dialog consults
 * `MatDialog.openDialogs` in its own `canDeactivate` and runs this wrapper's
 * gate (delegated to the inner editor) before its own — otherwise MatDialog's
 * `closeOnNavigation` would silently dispose a dirty session.
 */
@Component({
  selector: 'ts-pipeline-editor-dialog',
  template: `<ts-pipeline-editor
    #editor
    tsDialogDismissGuard
    (dismissRequested)="editor.onCancel()"
  ></ts-pipeline-editor>`,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, PipelineEditorComponent, DialogDismissGuardDirective],
  providers: [{ provide: EDITOR_COMMIT_MODE, useValue: 'stage' }],
})
export class PipelineEditorDialogComponent implements OnInit, OnDestroy {
  @ViewChild(PipelineEditorComponent) private editor?: PipelineEditorComponent;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional()
    public readonly dialogRef: MatDialogRef<PipelineEditorDialogComponent, PipelineEditorOutcome | undefined> | null,
    private readonly effects: PipelineEditorEffects
  ) {}

  ngOnInit(): void {
    this.effects.outcome$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((outcome) => this.dialogRef?.close(outcome));
  }

  /** Delegates the navigation exit gate to the inner editor's dirty check. */
  canDeactivate(): Observable<boolean> | boolean {
    return this.editor ? this.editor.canDeactivate() : true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
